import { useEffect } from 'react'
import type { NextRouter } from 'next/router'

function getInteractiveAncestor(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null

  // Common interactive elements + ARIA fallbacks
  return (target as Element).closest(
    [
      'button',
      'a[href]',
      'input',
      'select',
      'textarea',
      '[role="button"]',
      '[role="link"]',
      // Explicit opt-out for rare cases where we want interaction in preview
      '[data-preview-allow-interaction="true"]',
    ].join(', '),
  ) as HTMLElement | null
}

function isAllowedInPreview(el: HTMLElement | null): boolean {
  if (!el) return false
  return Boolean(el.closest('[data-preview-allow-interaction="true"]'))
}

/**
 * PreviewInteractionGuard
 *
 * When `?preview=true`, prevent user interactions (clicks, submits, keyboard activation)
 * on interactive elements. This is used for hosted page previews embedded in an iframe,
 * where actions (e.g. cancelling a purchase) should never execute.
 */
export default function PreviewInteractionGuard({ router }: { router: NextRouter }) {
  const isPreview = router.query.preview === 'true'

  useEffect(() => {
    if (typeof window === 'undefined') return

    const root = document.documentElement
    if (isPreview) root.setAttribute('data-preview', 'true')
    else root.removeAttribute('data-preview')

    if (!isPreview) return

    const blockIfInteractive = (event: Event) => {
      const interactive = getInteractiveAncestor(event.target)
      if (!interactive) return
      if (isAllowedInPreview(interactive)) return

      event.preventDefault()
      // Stop both native + React handlers
      event.stopPropagation()
      event.stopImmediatePropagation?.()
    }

    const onKeyDownCapture = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      const interactive = getInteractiveAncestor(event.target)
      if (!interactive) return
      if (isAllowedInPreview(interactive)) return

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation?.()
    }

    // Use capture listeners so we stop actions before app handlers run.
    document.addEventListener('click', blockIfInteractive, true)
    document.addEventListener('submit', blockIfInteractive, true)
    document.addEventListener('keydown', onKeyDownCapture, true)

    return () => {
      document.removeEventListener('click', blockIfInteractive, true)
      document.removeEventListener('submit', blockIfInteractive, true)
      document.removeEventListener('keydown', onKeyDownCapture, true)
      root.removeAttribute('data-preview')
    }
  }, [isPreview])

  return null
}
